import { useRef } from 'react';
import cx from 'classnames';
import useOnClickOutside from 'use-onclickoutside';

import { TooltipV2 } from '@motorway/motorway-storybook-cra';

import { useBreakpoint } from 'Context/BreakpointsContext/BreakpointsContext';
import { useIsHoveringSavedSearchesChip } from 'Stores/SavedFiltersActionsStore/SavedFiltersActionsStore';
import useSavedFiltersStore, { useCurrentFilter } from 'Stores/SavedFiltersStore/SavedFiltersStore';

import { SavedSearchPill } from '../SavedSearchPill/SavedSearchPill';
import {
	triggerSavedSearchPillClickGAEvent,
	triggerSavedSearchPillHoverGAEvent,
} from '../SavedSearchPill/SavedSearchPill.helpers';
import { SavedSearchPopoverContent } from '../SavedSearchPopoverContent/SavedSearchPopoverContent';

import styles from './SavedSearchPopover.module.scss';

const popperModifiers = [
	{
		enabled: false,
		name: 'flip',
	},
];

const popoverOffset = [0, 5];

export const SavedSearchPopover: React.FC = () => {
	const savedSearchPopoverRef = useRef<HTMLDivElement | null>(null);
	const {
		minWidth: { breakpointContent: isDesktop },
	} = useBreakpoint();
	const { isSavedSearchPillOpen, setIsSavedSearchPillOpen } = useSavedFiltersStore((store) => ({
		isSavedSearchPillOpen: store.isSavedSearchPillOpen,
		setIsSavedSearchPillOpen: store.setIsSavedSearchPillOpen,
	}));
	const isHoveringSavedSearchesChip = useIsHoveringSavedSearchesChip();
	const shouldShowPopover = isDesktop && isSavedSearchPillOpen;

	const hasActiveSavedSearch = !!useCurrentFilter();

	useOnClickOutside(savedSearchPopoverRef, () => {
		const shouldClosePopover = isSavedSearchPillOpen && !isHoveringSavedSearchesChip;
		if (shouldClosePopover) {
			setIsSavedSearchPillOpen(false);
		}
	});

	return (
		<TooltipV2
			alwaysVisible={true}
			arrowClassName={styles.arrow}
			content={
				<div ref={savedSearchPopoverRef} className={styles.savedSearchPopoverContent}>
					<SavedSearchPopoverContent />
				</div>
			}
			contentClassName={cx(styles.savedSearchPopover, { [styles.savedSearchPopoverHide]: !shouldShowPopover })}
			extraModifiers={popperModifiers}
			id="saved-search-popover"
			offset={popoverOffset}
			placement="bottom-start"
			renderInPortal={true}
			trigger="click"
			triggerProps={{
				onMouseEnter: () => triggerSavedSearchPillHoverGAEvent(hasActiveSavedSearch),
			}}
		>
			<SavedSearchPill onClick={() => triggerSavedSearchPillClickGAEvent(hasActiveSavedSearch)} />
		</TooltipV2>
	);
};
